import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "motion/react";
import { toast } from "react-toastify";
import clientRequests from "../../api/ClientRequests";
import Loader from "../../components/utils/Loader";
import DataTable from "../../components/utils/DataTable";
import Navbar from "../../components/dashboard/Navbar";
import {
  Users as UsersIcon,
  Eye,
  Trash2,
  Mail,
  Phone,
  MapPin,
  Calendar,
  AlertCircle,
  CheckCircle,
  XCircle,
  Search,
  ChevronLeft,
  ChevronRight,
  Filter,
  Coins,
  Crown,
  UserCheck,
  MessageSquare,
  CreditCard,
  RotateCcw,
  Copy,
  History,
  X,
  Download,
  Loader2,
} from "lucide-react";

const LIMIT_OPTIONS = [15, 25, 50, 100];
const SORT_OPTIONS = [
  { value: "asc", label: "Ascending" },
  { value: "desc", label: "Descending" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All users" },
  { value: "1", label: "Active" },
  { value: "0", label: "Deactivated" },
  { value: "inactive", label: "Inactive" },
];
const DAY_PRESETS = [30, 60, 90, 180, 365];
const DEFAULT_DAYS = 90;

const parseDays = (value) => {
  const n = parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : DEFAULT_DAYS;
};

const getParamsFromUrl = (searchParams) => ({
  page: Math.max(1, parseInt(searchParams.get("page") || "1", 10)),
  limit: LIMIT_OPTIONS.includes(parseInt(searchParams.get("limit"), 10))
    ? parseInt(searchParams.get("limit"), 10)
    : 15,
  search: searchParams.get("search") || "",
  sort_order: searchParams.get("sort_order") || "asc",
  date_from: searchParams.get("date_from") || "",
  date_to: searchParams.get("date_to") || "",
  // "1" = active, "0" = deactivated, "inactive" = no SMS / subscription activity within the day thresholds
  status: STATUS_OPTIONS.some((o) => o.value === searchParams.get("status"))
    ? searchParams.get("status")
    : "",
  sms_inactive_days: parseDays(searchParams.get("sms_inactive_days")),
  subscription_inactive_days: parseDays(
    searchParams.get("subscription_inactive_days")
  ),
});

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString() : null;

const filterSelectClass =
  "w-full rounded-xl border border-gray-200 bg-gray-50/50 py-2.5 pl-3 pr-9 text-sm text-gray-900 transition focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:opacity-50";

// Preset dropdown with a "Custom" option that reveals a number input
const DaysFilter = ({ label, value, onChange }) => {
  const [custom, setCustom] = useState(!DAY_PRESETS.includes(value));
  const [draft, setDraft] = useState(String(value));

  useEffect(() => {
    setDraft(String(value));
    if (!DAY_PRESETS.includes(value)) setCustom(true);
  }, [value]);

  const commitDraft = () => {
    const n = parseInt(draft, 10);
    if (Number.isFinite(n) && n > 0 && n !== value) onChange(n);
    else setDraft(String(value));
  };

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium tracking-wider text-gray-500 uppercase">
        {label}
      </label>
      <select
        value={custom ? "custom" : value}
        onChange={(e) => {
          if (e.target.value === "custom") {
            setCustom(true);
          } else {
            setCustom(false);
            onChange(Number(e.target.value));
          }
        }}
        className={filterSelectClass}
      >
        {DAY_PRESETS.map((d) => (
          <option key={d} value={d}>
            {d}+ days
          </option>
        ))}
        <option value="custom">Custom…</option>
      </select>
      {custom && (
        <input
          type="number"
          min={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitDraft}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitDraft();
          }}
          placeholder="Number of days"
          className="w-full rounded-xl border border-gray-200 bg-gray-50/50 py-2.5 px-3 text-sm text-gray-900 transition focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
        />
      )}
    </div>
  );
};

const ActivityCell = ({ days, lastAt, icon: Icon, label }) => (
  <div className="flex gap-2 items-start">
    <Icon className="mt-0.5 w-3.5 h-3.5 text-gray-400" />
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm font-medium text-gray-900">
        {lastAt ? formatDate(lastAt) : "Never"}
      </p>
      <p className="text-xs text-amber-600">
        {days != null ? `${Number(days).toLocaleString()} days ago` : "—"}
      </p>
    </div>
  </div>
);

// ─── User details modal helpers ─────────────────────────────────────────────
const formatDateTime = (value) =>
  value
    ? `${new Date(value).toLocaleDateString()} · ${new Date(value).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`
    : null;

const daysAgoLabel = (days) =>
  days != null ? `${Number(days).toLocaleString()} days ago` : null;

const SUMMARY_TONES = {
  amber: "bg-amber-50 border-amber-100 text-amber-600",
  blue: "bg-blue-50 border-blue-100 text-blue-600",
  gray: "bg-gray-50 border-gray-200 text-gray-500",
};

const SummaryCard = ({ icon: Icon, label, value, tone = "gray" }) => (
  <div className={`flex gap-3 items-center p-4 rounded-xl border ${SUMMARY_TONES[tone]}`}>
    <div className="flex flex-shrink-0 justify-center items-center w-10 h-10 bg-white rounded-lg shadow-sm">
      <Icon className="w-5 h-5" />
    </div>
    <div className="min-w-0">
      <p className="text-xs font-medium">{label}</p>
      <p className="text-lg font-bold text-gray-900 truncate">{value}</p>
    </div>
  </div>
);

const DetailSection = ({ title, icon: Icon, children }) => (
  <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/60">
    <div className="flex gap-2 items-center mb-3">
      <Icon className="w-4 h-4 text-gray-400" />
      <h5 className="text-xs font-semibold tracking-wider text-gray-500 uppercase">
        {title}
      </h5>
    </div>
    <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">{children}</div>
  </div>
);

const DetailField = ({ label, value, hint, hintWarn, mono, wide, action }) => {
  const isEmpty = value === null || value === undefined || value === "";
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <p className="mb-0.5 text-xs text-gray-500">{label}</p>
      <div className="flex gap-1.5 items-center">
        <p
          className={`text-sm break-all ${
            isEmpty ? "text-gray-400" : "font-medium text-gray-900"
          } ${mono ? "font-mono text-xs" : ""}`}
        >
          {isEmpty ? "—" : value}
        </p>
        {action}
      </div>
      {hint && (
        <p className={`mt-0.5 text-xs ${hintWarn ? "font-medium text-amber-600" : "text-gray-500"}`}>
          {hint}
        </p>
      )}
    </div>
  );
};

const Users = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [verifyingUserSlug, setVerifyingUserSlug] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const params = getParamsFromUrl(searchParams);

  const updateUrl = useCallback(
    (updates) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (value === "" || value == null || value === undefined) {
            next.delete(key);
          } else {
            next.set(key, String(value));
          }
        });
        next.set("page", String(updates.page ?? next.get("page") ?? 1));
        return next;
      });
    },
    [setSearchParams]
  );

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      // Day thresholds only apply to status "inactive"
      const isInactiveStatus = params.status === "inactive";
      const response = await clientRequests.getAllClients({
        page: params.page,
        limit: params.limit,
        search: params.search || undefined,
        sort_order: params.sort_order,
        date_from: params.date_from || undefined,
        date_to: params.date_to || undefined,
        status: params.status || undefined,
        sms_inactive_days: isInactiveStatus
          ? params.sms_inactive_days
          : undefined,
        subscription_inactive_days: isInactiveStatus
          ? params.subscription_inactive_days
          : undefined,
      });

      if (response.status && response.data != null) {
        // Response structure: response.data.data.data.data (array) + .pagination
        const inner = response.data?.data?.data;
        const list = inner && Array.isArray(inner.data) ? inner.data : [];
        const paginationMeta = inner?.pagination ?? null;
        setUsers(list);
        setPagination(paginationMeta);
      } else {
        const reason =
          response.data?.data?.reason ??
          response.data?.reason ??
          response.message ??
          "Failed to fetch users";
        toast.error(reason);
        setUsers([]);
        setPagination(null);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to fetch users");
      setUsers([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [
    params.page,
    params.limit,
    params.search,
    params.sort_order,
    params.date_from,
    params.date_to,
    params.status,
    params.sms_inactive_days,
    params.subscription_inactive_days,
  ]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    setSearchInput(params.search);
  }, [params.search]);

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleToggleStatus = async (user) => {
    try {
      const currentStatus =
        user.status === 1 || user.status === "1" || user.status === "active";
      const newStatus = !currentStatus;

      const response = await clientRequests.updateUserStatus(
        user.user_slug,
        newStatus
      );

      if (response.status) {
        setUsers((prevUsers) =>
          prevUsers.map((u) =>
            u.user_slug === user.user_slug
              ? { ...u, status: newStatus ? 1 : 0 }
              : u
          )
        );
        toast.success(
          `User ${newStatus ? "activated" : "deactivated"} successfully!`
        );
      } else {
        toast.error(response.message || "Failed to update user status");
      }
    } catch (error) {
      console.error("Error updating user status:", error);
      toast.error("Failed to update user status");
    }
  };

  const handleVerifyUser = async (user) => {
    if (verifyingUserSlug === user.user_slug) return;
    setVerifyingUserSlug(user.user_slug);
    try {
      const response = await clientRequests.verifyUser(user.user_slug);
      if (response.status) {
        setUsers((prevUsers) =>
          prevUsers.map((existingUser) =>
            existingUser.user_slug === user.user_slug
              ? { ...existingUser, email_verified_at: new Date().toISOString() }
              : existingUser
          )
        );
        toast.success("User verified successfully!");
      } else {
        toast.error(response.message || "Failed to verify user");
      }
    } catch (error) {
      console.error("Error verifying user:", error);
      toast.error("Failed to verify user");
    } finally {
      setVerifyingUserSlug(null);
    }
  };

  const confirmDelete = async () => {
    if (!selectedUser) return;
    setDeleteLoading(true);
    try {
      const response = await clientRequests.deleteUser(selectedUser.user_slug);
      if (response.status) {
        toast.success("User deleted successfully!");
        setShowDeleteModal(false);
        setSelectedUser(null);
        fetchUsers();
      } else {
        toast.error(response.message || "Failed to delete user");
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error("Failed to delete user");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== params.search) {
        updateUrl({ search: searchInput || "", page: 1 });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput, params.search, updateUrl]);

  const handleSearchSubmit = (e) => {
    e?.preventDefault();
    updateUrl({ search: searchInput || "", page: 1 });
  };

  const handlePageChange = (page) => {
    const last = pagination?.last_page ?? 1;
    const next = Math.max(1, Math.min(page, last));
    updateUrl({ page: next });
  };

  // Export every user matching the current filters (not just this page) as CSV
  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    const toastId = toast.loading("Preparing export...");
    const isInactiveStatus = params.status === "inactive";
    try {
      const result = await clientRequests.exportUsers({
        search: params.search || undefined,
        sort_order: params.sort_order,
        date_from: params.date_from || undefined,
        date_to: params.date_to || undefined,
        status: params.status || undefined,
        sms_inactive_days: isInactiveStatus
          ? params.sms_inactive_days
          : undefined,
        subscription_inactive_days: isInactiveStatus
          ? params.subscription_inactive_days
          : undefined,
      });
      toast.update(toastId, {
        render: result.status
          ? "Export downloaded successfully!"
          : result.message || "Export failed",
        type: result.status ? "success" : "error",
        isLoading: false,
        autoClose: result.status ? 3000 : 5000,
      });
    } catch (error) {
      console.error("Error exporting users:", error);
      toast.update(toastId, {
        render: "Failed to export users",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleResetFilters = () => {
    setSearchInput("");
    setSearchParams(new URLSearchParams({ page: "1" }));
  };

  const isInactiveView = params.status === "inactive";
  const isFiltered =
    params.search !== "" ||
    params.limit !== 15 ||
    params.sort_order !== "asc" ||
    params.date_from !== "" ||
    params.date_to !== "" ||
    params.status !== "";

  // Pagination meta — pulled directly from API response
  const total = pagination?.total ?? 0;
  const lastPage = pagination?.last_page ?? 1;
  const currentPage = pagination?.current_page ?? params.page;
  const from = pagination?.from ?? 0;
  const to = pagination?.to ?? 0;
  const perPage = pagination?.per_page ?? params.limit;

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedUser(null);
  };

  const closeUserModal = () => {
    setShowUserModal(false);
    setSelectedUser(null);
  };

  // Build visible page numbers (window of 5 around current)
  const getPageNumbers = () => {
    if (lastPage <= 7) return Array.from({ length: lastPage }, (_, i) => i + 1);
    const pages = [];
    const left = Math.max(2, currentPage - 2);
    const right = Math.min(lastPage - 1, currentPage + 2);
    pages.push(1);
    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < lastPage - 1) pages.push("...");
    pages.push(lastPage);
    return pages;
  };

  // ─── Table columns ────────────────────────────────────────────────────────
  const columns = [
    {
      key: "full_name",
      header: "User",
      sortable: true,
      render: (value, row) => (
        <div className="flex gap-3 items-center">
          <div className="flex-shrink-0">
            <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full">
              <span className="text-sm font-semibold text-white">
                {row.full_name?.charAt(0)?.toUpperCase() ||
                  row.email?.charAt(0)?.toUpperCase() ||
                  "U"}
              </span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {row.full_name || row.email?.split("@")[0] || "Unknown User"}
            </p>
            <p className="text-xs text-gray-500 truncate">{row.email}</p>
            <p className="text-xs font-medium text-blue-600">
              ID: {row.user_slug?.slice(0, 8)}...
            </p>
          </div>
        </div>
      ),
    },
    {
      key: "contact",
      header: "Contact Information",
      sortable: true,
      render: (value, row) => (
        <div className="space-y-2">
          {row.phone_number && (
            <div className="flex gap-2 items-center">
              <Phone className="w-3 h-3 text-gray-400" />
              <span className="text-sm font-medium text-gray-900">
                {row.phone_number}
              </span>
            </div>
          )}
          {row.email && (
            <div className="flex gap-2 items-center">
              <Mail className="w-3 h-3 text-gray-400" />
              <span className="text-sm text-gray-600 truncate max-w-[200px]">
                {row.email}
              </span>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "plan_name",
      header: "Plan & Units",
      sortable: true,
      render: (value, row) => (
        <div className="space-y-2">
          {/* Plan name badge */}
          <div className="flex gap-1.5 items-center">
            <Crown className="w-3.5 h-3.5 text-amber-500" />
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
              {row.plan_name || "No Plan"}
            </span>
          </div>
          {/* Units */}
          <div className="flex gap-1.5 items-center">
            <Coins className="w-3.5 h-3.5 text-blue-400" />
            <span className="text-sm font-semibold text-gray-800">
              {(row.units ?? 0).toLocaleString()}
              <span className="ml-1 text-xs font-normal text-gray-400">units</span>
            </span>
          </div>
          {/* Previous units (subtle) */}
          {row.previous_units > 0 && (
            <p className="text-xs text-gray-400">
              Prev: {row.previous_units.toLocaleString()}
            </p>
          )}
        </div>
      ),
    },
    {
      key: "last_sms_sent_at",
      header: "Last SMS",
      sortable: true,
      render: (value, row) => (
        <ActivityCell
          icon={MessageSquare}
          label="Last sent"
          lastAt={row.last_sms_sent_at}
          days={row.sms_inactive_days}
        />
      ),
    },
    {
      key: "last_subscription_purchase_at",
      header: "Last Subscription",
      sortable: true,
      render: (value, row) => (
        <ActivityCell
          icon={CreditCard}
          label="Last purchase"
          lastAt={row.last_subscription_purchase_at}
          days={row.subscription_inactive_days}
        />
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (value, row) => {
        const isActive =
          row.status === 1 || row.status === "1" || row.status === "active";
        return (
          <div className="flex gap-2 items-center">
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                isActive
                  ? "text-green-800 bg-green-100"
                  : "text-orange-800 bg-orange-100"
              }`}
            >
              {isActive ? (
                <>
                  <CheckCircle className="w-3 h-3" />
                  Active
                </>
              ) : (
                <>
                  <XCircle className="w-3 h-3" />
                  Deactivated
                </>
              )}
            </span>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleToggleStatus(row)}
              className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              title={isActive ? "Deactivate User" : "Activate User"}
            >
              {isActive ? (
                <XCircle className="w-4 h-4" />
              ) : (
                <CheckCircle className="w-4 h-4 text-green-500" />
              )}
            </motion.button>
          </div>
        );
      },
    },
    {
      key: "created_at",
      header: "Account Details",
      sortable: true,
      render: (value, row) => (
        <div className="space-y-1">
          <div className="flex gap-2 items-center">
            <Calendar className="w-3.5 h-3.5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">
                {row.created_at
                  ? new Date(row.created_at).toLocaleDateString()
                  : "Unknown"}
              </p>
              <p className="text-xs text-gray-500">
                {row.created_at
                  ? new Date(row.created_at).toLocaleTimeString()
                  : ""}
              </p>
            </div>
          </div>
          {row.email_verified_at && (
            <p className="text-xs text-green-600 pl-5">
              Verified: {new Date(row.email_verified_at).toLocaleDateString()}
            </p>
          )}
        </div>
      ),
    },
  ];

  const actions = [
    {
      icon: <Eye className="w-4 h-4" />,
      title: "View User",
      onClick: handleViewUser,
      variant: "primary",
    },
    {
      icon: <UserCheck className="w-4 h-4" />,
      title: "Verify User",
      onClick: handleVerifyUser,
      variant: "success",
      show: (row) => !row.email_verified_at,
    },
    {
      icon: <Trash2 className="w-4 h-4" />,
      title: "Delete User",
      onClick: handleDeleteUser,
      variant: "danger",
    },
  ];

  if (loading && users.length === 0) {
    return (
      <>
        <Navbar />
        <div className="flex justify-center items-center min-h-screen bg-white">
          <Loader />
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50">
        <div className="px-4 py-8 mx-auto max-w-8xl sm:px-6 lg:px-8">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="flex gap-3 items-center mb-3">
                  <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                    <UsersIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">
                      User Management
                    </h1>
                    <p className="text-gray-600">
                      Manage all registered users and their account status
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 items-center text-sm">
                  <span className="flex gap-2 items-center font-medium text-blue-600">
                    <div className="w-2 h-2 bg-blue-600 rounded-full" />
                    {pagination
                      ? `${total.toLocaleString()} ${
                          isInactiveView ? "inactive" : "total"
                        } ${total === 1 ? "user" : "users"}`
                      : "Loading..."}
                  </span>
                  {/* <span className="text-gray-400">|</span> */}
                  {/* <span className="text-gray-500">
                    Page {currentPage} of {lastPage || 1}
                  </span> */}
                  {/* {total > 0 && (
                    <>
                      <span className="text-gray-400">|</span>
                      <span className="text-gray-500">
                        Showing {from}–{to} of {total.toLocaleString()}
                      </span>
                    </>
                  )} */}
                </div>
              </div>
              <motion.button
                whileHover={!isExporting ? { scale: 1.03 } : {}}
                whileTap={!isExporting ? { scale: 0.97 } : {}}
                onClick={handleExport}
                disabled={isExporting || total === 0}
                title={
                  total === 0
                    ? "No users match the current filters"
                    : "Export all users matching the current filters as CSV"
                }
                className="flex gap-2 justify-center items-center self-start px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-xl shadow-sm transition-colors sm:self-auto hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isExporting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                {isExporting ? "Exporting…" : "Export CSV"}
              </motion.button>
            </div>
          </motion.div>

          {/* Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="overflow-hidden mb-6 bg-white rounded-2xl border ring-1 ring-gray-100 shadow-sm border-gray-200/80"
          >
            <div className="flex flex-wrap gap-3 justify-between items-center px-5 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
              <div className="flex gap-2 items-center">
                <div className="flex justify-center items-center w-9 h-9 text-blue-600 bg-blue-100 rounded-xl">
                  <Filter className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">
                    Filters & search
                  </h2>
                  <p className="text-xs text-gray-500">
                    Refine the user list with search and filters
                  </p>
                </div>
              </div>
              {isFiltered && (
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="flex gap-1.5 items-center px-3 py-2 text-sm font-medium text-gray-600 rounded-lg transition-colors hover:bg-gray-100 hover:text-gray-900"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset filters
                </button>
              )}
            </div>
            <div className="p-5 sm:p-6">
              <form onSubmit={handleSearchSubmit} className="mb-5">
                <label className="block mb-2 text-xs font-medium tracking-wider text-gray-500 uppercase">
                  Search
                </label>
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 w-5 h-5 text-gray-400 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search by name, email, or phone..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="py-3 pr-4 pl-11 w-full placeholder-gray-400 text-gray-900 rounded-xl border border-gray-200 transition bg-gray-50/50 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
              </form>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                <div className="space-y-2">
                  <label className="block text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Per page
                  </label>
                  <select
                    value={params.limit}
                    onChange={(e) =>
                      updateUrl({ limit: Number(e.target.value), page: 1 })
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50/50 py-2.5 pl-3 pr-9 text-sm text-gray-900 transition focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    {LIMIT_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="block text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Sort order
                  </label>
                  <select
                    value={params.sort_order}
                    onChange={(e) =>
                      updateUrl({ sort_order: e.target.value, page: 1 })
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50/50 py-2.5 pl-3 pr-9 text-sm text-gray-900 transition focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    {SORT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="block text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Status
                  </label>
                  <select
                    value={params.status}
                    onChange={(e) => {
                      const status = e.target.value;
                      updateUrl({
                        status,
                        // Day thresholds only apply to the inactive view
                        sms_inactive_days:
                          status === "inactive" ? params.sms_inactive_days : "",
                        subscription_inactive_days:
                          status === "inactive"
                            ? params.subscription_inactive_days
                            : "",
                        page: 1,
                      });
                    }}
                    className={filterSelectClass}
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="block text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Date from
                  </label>
                  <input
                    type="date"
                    value={params.date_from}
                    onChange={(e) =>
                      updateUrl({ date_from: e.target.value, page: 1 })
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50/50 py-2.5 px-3 text-sm text-gray-900 transition focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Date to
                  </label>
                  <input
                    type="date"
                    value={params.date_to}
                    onChange={(e) =>
                      updateUrl({ date_to: e.target.value, page: 1 })
                    }
                    className="w-full rounded-xl border border-gray-200 bg-gray-50/50 py-2.5 px-3 text-sm text-gray-900 transition focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
                <div className="flex flex-col justify-end">
                  <div className="px-4 py-3 text-center rounded-xl border border-gray-100 bg-gray-50/80">
                    <p className="text-xs font-medium text-gray-500">Results</p>
                    <p className="mt-0.5 text-sm font-semibold text-gray-900">
                      {from}–{to}{" "}
                      <span className="font-normal text-gray-500">
                        of {total.toLocaleString()}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Inactivity thresholds (status "inactive" only) */}
              {isInactiveView && (
                <div className="grid grid-cols-1 gap-4 pt-5 mt-5 border-t border-gray-100 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                  <DaysFilter
                    label="No SMS sent for"
                    value={params.sms_inactive_days}
                    onChange={(days) =>
                      updateUrl({ sms_inactive_days: days, page: 1 })
                    }
                  />
                  <DaysFilter
                    label="No subscription for"
                    value={params.subscription_inactive_days}
                    onChange={(days) =>
                      updateUrl({ subscription_inactive_days: days, page: 1 })
                    }
                  />
                </div>
              )}
            </div>
          </motion.div>

          {/* DataTable */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-xl"
          >
            <DataTable
              data={users}
              columns={columns}
              actions={actions}
              searchable={false}
              filterable={false}
              exportable={false}
              pagination={false}
              loading={loading}
              emptyMessage="No users found"
              showStats={false}
            />

            {/* ── Server-side Pagination ── */}
            {pagination && lastPage > 1 && (
              <div className="flex flex-wrap gap-4 justify-between items-center px-6 py-4 bg-gray-50 border-t border-gray-200">
                {/* Left: summary */}
                <div className="text-sm text-gray-600">
                  Showing{" "}
                  <span className="font-semibold text-gray-900">
                    {from}–{to}
                  </span>{" "}
                  of{" "}
                  <span className="font-semibold text-gray-900">
                    {total.toLocaleString()}
                  </span>{" "}
                  users &nbsp;·&nbsp; Page{" "}
                  <span className="font-semibold text-gray-900">
                    {currentPage}
                  </span>{" "}
                  of{" "}
                  <span className="font-semibold text-gray-900">{lastPage}</span>
                </div>

                {/* Right: page controls */}
                <div className="flex gap-1 items-center">
                  {/* First page */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage <= 1}
                    className="hidden sm:flex px-2.5 py-1.5 text-xs font-medium text-gray-600 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title="First page"
                  >
                    «
                  </motion.button>

                  {/* Prev */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="p-2 text-gray-600 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title="Previous page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </motion.button>

                  {/* Page number buttons */}
                  {getPageNumbers().map((p, i) =>
                    p === "..." ? (
                      <span
                        key={`ellipsis-${i}`}
                        className="px-2 py-1.5 text-sm text-gray-400 select-none"
                      >
                        …
                      </span>
                    ) : (
                      <motion.button
                        key={p}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handlePageChange(p)}
                        className={`min-w-[2.25rem] py-1.5 text-sm font-medium rounded-lg transition-colors ${
                          currentPage === p
                            ? "bg-blue-600 text-white shadow-sm"
                            : "text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        {p}
                      </motion.button>
                    )
                  )}

                  {/* Next */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= lastPage}
                    className="p-2 text-gray-600 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title="Next page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </motion.button>

                  {/* Last page */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handlePageChange(lastPage)}
                    disabled={currentPage >= lastPage}
                    className="hidden sm:flex px-2.5 py-1.5 text-xs font-medium text-gray-600 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title="Last page"
                  >
                    »
                  </motion.button>

                  {/* Jump to page */}
                  <div className="hidden lg:flex gap-1 items-center ml-3 pl-3 border-l border-gray-200">
                    <span className="text-xs text-gray-500">Go to</span>
                    <input
                      type="number"
                      min={1}
                      max={lastPage}
                      defaultValue={currentPage}
                      key={currentPage}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          const val = parseInt(e.target.value, 10);
                          if (!isNaN(val)) handlePageChange(val);
                        }
                      }}
                      className="w-14 px-2 py-1.5 text-sm text-center border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20"
                    />
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* ── User Details Modal ── */}
      {showUserModal && selectedUser && (() => {
        const u = selectedUser;
        const isActive = u.status === 1 || u.status === "1";
        return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-[9998]"
            onClick={closeUserModal}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative z-[9999] flex flex-col w-full max-w-3xl max-h-[90vh] overflow-hidden text-left bg-white rounded-2xl shadow-2xl"
          >
            {/* Header band */}
            <div className="relative px-6 pt-6 pb-5 bg-gradient-to-br from-blue-50 via-indigo-50 to-white border-b border-gray-100">
              <button
                onClick={closeUserModal}
                className="absolute top-4 right-4 p-1.5 text-gray-400 rounded-lg transition-colors hover:text-gray-700 hover:bg-white/80"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex gap-4 items-center pr-8">
                <div className="flex flex-shrink-0 justify-center items-center w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-md">
                  <span className="text-2xl font-bold text-white">
                    {u.full_name?.charAt(0)?.toUpperCase() ||
                      u.email?.charAt(0)?.toUpperCase() ||
                      "U"}
                  </span>
                </div>
                <div className="min-w-0">
                  <h3 className="text-xl font-semibold text-gray-900 truncate">
                    {u.full_name || u.email?.split("@")[0] || "Unknown User"}
                  </h3>
                  <p className="text-sm text-gray-600 truncate">{u.email}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        u.email_verified_at
                          ? "bg-green-100 text-green-800"
                          : "bg-orange-100 text-orange-800"
                      }`}
                    >
                      {u.email_verified_at ? (
                        <CheckCircle className="w-3 h-3" />
                      ) : (
                        <XCircle className="w-3 h-3" />
                      )}
                      {u.email_verified_at ? "Verified" : "Unverified"}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-orange-100 text-orange-800"
                      }`}
                    >
                      {isActive ? (
                        <CheckCircle className="w-3 h-3" />
                      ) : (
                        <XCircle className="w-3 h-3" />
                      )}
                      {isActive ? "Active" : "Deactivated"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Scrollable body */}
            <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
              {/* Summary cards */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <SummaryCard
                  icon={Crown}
                  label="Plan"
                  value={u.plan_name || "No Plan"}
                  tone="amber"
                />
                <SummaryCard
                  icon={Coins}
                  label="Units"
                  value={(u.units ?? 0).toLocaleString()}
                  tone="blue"
                />
                <SummaryCard
                  icon={History}
                  label="Previous Units"
                  value={(u.previous_units ?? 0).toLocaleString()}
                  tone="gray"
                />
              </div>

              <DetailSection title="Activity" icon={MessageSquare}>
                <DetailField
                  label="Last SMS sent"
                  value={formatDateTime(u.last_sms_sent_at) || "Never"}
                  hint={daysAgoLabel(u.sms_inactive_days)}
                  hintWarn={u.sms_inactive_days >= 90}
                />
                <DetailField
                  label="Last subscription purchase"
                  value={formatDateTime(u.last_subscription_purchase_at) || "Never"}
                  hint={daysAgoLabel(u.subscription_inactive_days)}
                  hintWarn={u.subscription_inactive_days >= 90}
                />
              </DetailSection>

              <DetailSection title="Contact" icon={Phone}>
                <DetailField label="Phone number" value={u.phone_number} />
                <DetailField label="Email" value={u.email} />
                <DetailField label="User ID" value={u.id} />
                <DetailField
                  label="User slug"
                  value={u.user_slug}
                  mono
                  action={
                    u.user_slug && (
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard
                            ?.writeText(u.user_slug)
                            .then(() => toast.success("User slug copied"))
                            .catch(() => toast.error("Could not copy"));
                        }}
                        className="p-1 text-gray-400 rounded-md transition-colors hover:text-blue-600 hover:bg-blue-50"
                        title="Copy user slug"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    )
                  }
                />
              </DetailSection>

              <DetailSection title="Location" icon={MapPin}>
                <DetailField label="City" value={u.city} />
                <DetailField label="State" value={u.state} />
                <DetailField label="Address" value={u.address} wide />
              </DetailSection>

              <DetailSection title="Account" icon={Calendar}>
                <DetailField
                  label="Account created"
                  value={formatDateTime(u.created_at)}
                />
                <DetailField
                  label="Email verified"
                  value={formatDateTime(u.email_verified_at) || "Not verified"}
                />
                <DetailField
                  label="Notified"
                  value={u.is_notified ? "Yes" : "No"}
                />
                <DetailField
                  label="Status"
                  value={isActive ? "Active" : "Deactivated"}
                />
              </DetailSection>
            </div>

            {/* Sticky footer */}
            <div className="flex gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  closeUserModal();
                  handleToggleStatus(u);
                }}
                className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-xl transition-colors border ${
                  isActive
                    ? "text-red-700 bg-red-50 hover:bg-red-100 border-red-200"
                    : "text-green-700 bg-green-50 hover:bg-green-100 border-green-200"
                }`}
              >
                {isActive ? "Deactivate User" : "Activate User"}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={closeUserModal}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white rounded-xl border border-gray-200 transition-colors hover:bg-gray-100"
              >
                Close
              </motion.button>
            </div>
          </motion.div>
        </div>
        );
      })()}

      {/* ── Delete Confirmation Modal ── */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 z-[9999] overflow-y-auto">
          <div className="flex justify-center items-center px-4 pt-4 pb-20 min-h-screen text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 z-[9998]"
              onClick={closeDeleteModal}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative z-[9999] inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl"
            >
              <div className="flex gap-4 items-center mb-4">
                <AlertCircle className="w-8 h-8 text-red-600 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Delete User
                  </h3>
                  <p className="text-sm text-gray-600">
                    This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="p-4 mb-6 bg-red-50 rounded-lg space-y-2">
                <p className="text-sm text-red-800">
                  <strong>User:</strong>{" "}
                  {selectedUser.full_name || selectedUser.email || "Unknown User"}
                </p>
                <p className="text-sm text-red-700">
                  <strong>Email:</strong> {selectedUser.email}
                </p>
                <p className="text-sm text-red-700">
                  <strong>Phone:</strong>{" "}
                  {selectedUser.phone_number || "Not provided"}
                </p>
                <p className="text-sm text-red-700">
                  <strong>Plan:</strong> {selectedUser.plan_name || "No Plan"}
                </p>
                <p className="text-sm text-red-700">
                  <strong>Units:</strong>{" "}
                  {(selectedUser.units ?? 0).toLocaleString()}
                </p>
              </div>

              <div className="flex gap-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={confirmDelete}
                  disabled={deleteLoading}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg transition-colors hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteLoading ? "Deleting..." : "Delete User"}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={closeDeleteModal}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
                >
                  Cancel
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </>
  );
};

export default Users;